/**
 * Feature registry — labels and ordering for the palette's feature groups.
 *
 * Every palette result belongs to exactly one feature. This file owns
 * the display label and the priority order. Priority mirrors the
 * AppHeader nav so the palette's grouping matches the app's chrome.
 *
 * Feature ids are derived from result ids when not set explicitly:
 *   "scope:notes.categories" → "notes"
 *   "cmd:workspace.split-pane" → "workspace"
 *   "conv:abc123" → falls back to "app" unless the provider sets feature
 *
 * Cross-cutting actions (theme, palette ops, dev tools) carry
 * feature: "app" and land in the "App" group, anchored at the end.
 */
import type { FeatureKey } from "../types";

/** Canonical display label per feature. */
export const FEATURE_LABEL: Record<string, string> = {
  chat: "Chat",
  notes: "Notes",
  tasks: "Tasks",
  srs: "SRS",
  reading: "Reading",
  "mental-math": "Mental Math",
  cognition: "Cognition",
  nback: "N-Back",
  relational: "Relational",
  battery: "Check-in",
  meditation: "Meditation",
  "input-types": "Input Types",
  "app-usage": "App Usage",
  "app-blocking": "App Blocking",
  spaces: "Spaces",
  workspace: "Workspace",
  admin: "Admin",
  app: "App",
};

/** Tiebreaker order — used when two features have the same top score.
 *  Mirrors the AppHeader nav, with cross-cutting groups anchored at end. */
export const FEATURE_ORDER: FeatureKey[] = [
  "chat",
  "notes",
  "tasks",
  "srs",
  "reading",
  "mental-math",
  "cognition",
  "nback",
  "relational",
  "battery",
  "meditation",
  "input-types",
  "app-usage",
  "app-blocking",
  "spaces",
  "workspace",
  "admin",
  "app",
];

/** Derive the feature for a result-id when no explicit feature is set.
 *
 *   "scope:notes.categories" → "notes"
 *   "scope:notes"            → "notes"
 *   "cmd:workspace.split"    → "workspace"
 *   "conv:abc123"            → "app" (provider should set explicit feature)
 */
export function featureFromId(id: string): FeatureKey {
  const colonIdx = id.indexOf(":");
  const tail = colonIdx >= 0 ? id.slice(colonIdx + 1) : id;
  const dotIdx = tail.indexOf(".");
  const head = dotIdx >= 0 ? tail.slice(0, dotIdx) : tail;
  return head in FEATURE_LABEL ? head : "app";
}

export function labelFor(feature: FeatureKey): string {
  return FEATURE_LABEL[feature] ?? feature;
}

export function priorityOf(feature: FeatureKey): number {
  const idx = FEATURE_ORDER.indexOf(feature);
  return idx < 0 ? FEATURE_ORDER.length : idx;
}
